import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { CSV_OPTIONS } from 'utils/csv';
import { formatPlural } from 'utils/format';
import { EditableSession, EventType } from 'types/event.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { useCsvReportDownload } from 'hooks/useCsvReportDownload';
import { Icon } from '@epoch-ui/components/Icon';
import { Button } from '@epoch-ui/components/Button';
import { ButtonLink } from '@epoch-ui/components/ButtonLink';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import {
  useEstimatedAttendanceModal,
  EventEstimatedAttendanceModal
} from '@event-editor/components/EventEstimatedAttendanceModal';
import { Tooltip, TooltipContent, TooltipTrigger } from '@epoch-ui/components/Tooltip';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { distrolistInvitesApi, communityInvitesApi } from '@event-editor/api/event-invites';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useInviteGuestsModal } from '@event-editor/hooks/eventEditorState';
import { useCanAccessDistrolists } from 'hooks/useCanAccessDistrolists';
import { useCanAccessCommunities, useCommunitiesLabel } from 'hooks/useCanAccessCommunities';

import { CheckInLinkButton } from '../CheckInLinkButton';

import { ManageDistrolistInvitesModal } from './ManageDistrolistInvitesModal';
import { ManageCommunityInvitesModal } from './ManageCommunityInvitesModal';

const GuestsSectionHeader: React.FC<{ session?: EditableSession }> = ({ session }) => {
  const event = useEditableEvent();
  const canAccessDistrolists = useCanAccessDistrolists();
  const canAccessCommunities = useCanAccessCommunities();
  const communitiesLabel = useCommunitiesLabel();
  const { showToast } = useToaster();
  const { open: openInviteGuestsModal } = useInviteGuestsModal();
  const { modalProps, open: openEstimatedAttendanceModal } = useEstimatedAttendanceModal({
    session
  });

  const [manageDistrolistInvitesModalOpen, setManageDistrolistInvitesModalOpen] = useState(false);

  const distrolistInvitesQuery = useQuery({
    queryKey: eventEditorQueryKeys.eventDistrolistInvites(event.id!),
    queryFn: () => distrolistInvitesApi.list(event.id!),
    enabled:
      !!event.id && event.isPublished && (event.totalGuests ?? 0) > 0 && canAccessDistrolists,
    throwOnError: false
  });

  const [manageCommunityInvitesModalOpen, setManageCommunityInvitesModalOpen] = useState(false);

  const communityInvitesQuery = useQuery({
    queryKey: eventEditorQueryKeys.eventCommunityInvites(event.id!),
    queryFn: () => communityInvitesApi.list(event.id!),
    enabled: !!event.id && (event.communityInviteeIds?.length ?? 0) > 0 && canAccessCommunities,
    throwOnError: false
  });
  const linkedCommunityCount = communityInvitesQuery.data?.length ?? 0;

  const { loading: csvLoading, triggerCsvReportDownload } = useCsvReportDownload({
    type: CSV_OPTIONS.GUESTS,
    exportParams: event.id!,
    onSuccess: (): void => {
      showToast('CSV downloaded');
    },
    onError: (errorMessage): void => {
      showToast(errorMessage, { variant: ToasterVariant.ERROR });
    }
  });

  const isMultiSessionEvent = event.eventType === EventType.MULTI_SESSIONS;
  const isGuestCheckinOpen = isMultiSessionEvent
    ? session?.isGuestCheckinOpen
    : event.isGuestCheckinOpen;
  const sessionHasEnded = isMultiSessionEvent ? session?.hasEnded : event.hasEnded;

  return (
    <div className="tw-flex tw-items-start">
      <div className="tw-mb-1 tw-flex tw-flex-1 tw-flex-col tw-gap-1">
        <h2 className="tw-m-0 tw-text-xl tw-font-medium tw-leading-7">
          Guests
          {event?.calendarNotifications && (event?.totalDeactivatedGuests || 0) > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Icon name="info" size={16} className="tw-text-medium-grey" />
              </TooltipTrigger>
              <TooltipContent className="tw-max-w-80">
                There may be discrepancies between this guest list and the calendar event because of
                deactivated users. Deactivated users are removed from the guest list on Epoch,
                resulting in a higher number of guests on the calendar event.
              </TooltipContent>
            </Tooltip>
          )}
        </h2>
        {canAccessDistrolists &&
          distrolistInvitesQuery.isLoading &&
          !distrolistInvitesQuery.data && <Skeleton className="tw-h-5 tw-w-3/4" />}
        {canAccessDistrolists &&
          !distrolistInvitesQuery.isLoading &&
          distrolistInvitesQuery.data &&
          distrolistInvitesQuery.data.length > 0 && (
            <p className="tw-m-0 tw-flex-1 tw-text-sm tw-text-primary-grey">
              You’re syncing {distrolistInvitesQuery.data?.length} distrolist
              {formatPlural(distrolistInvitesQuery.data?.length || 0)} with the guest list.{' '}
              <ButtonLink onClick={() => setManageDistrolistInvitesModalOpen(true)}>
                Manage distrolists
                <Icon name="arrow-up-right" size={16} className="tw-ml-1" />
              </ButtonLink>
            </p>
          )}
        {canAccessCommunities && linkedCommunityCount > 0 && (
          <p className="tw-m-0 tw-flex-1 tw-text-sm tw-text-primary-grey">
            {communitiesLabel} invited to this event: {linkedCommunityCount}.{' '}
            <ButtonLink onClick={() => setManageCommunityInvitesModalOpen(true)}>
              Manage {communitiesLabel}
              <Icon name="arrow-up-right" size={16} className="tw-ml-1" />
            </ButtonLink>
          </p>
        )}
      </div>

      {/* Guest list actions */}
      <div className="tw-flex tw-gap-2">
        {(event.totalGuests ?? 0) > 0 && (
          <Button
            variant="secondary"
            size="xxs"
            className="tw-rounded-xl"
            loading={csvLoading}
            disabled={csvLoading}
            onClick={() => triggerCsvReportDownload()}
          >
            <Icon name="arrow-down-to-line" size={16} className="tw-mr-1" />
            CSV
          </Button>
        )}
        {event.isUsingEstimatedAttendees && (
          <Button
            variant="secondary"
            size="xxs"
            className="tw-rounded-xl"
            onClick={() => openEstimatedAttendanceModal()}
          >
            <Icon name="circle-check" size={16} className="tw-mr-1" />
            Estimated attendees
          </Button>
        )}
        {!event.isUsingEstimatedAttendees &&
          (isGuestCheckinOpen || sessionHasEnded) &&
          !event.isThirtyDaysAfterEventEnded && (
            <CheckInLinkButton eventId={event.id!} sessionId={session?.id?.toString()} />
          )}
        {!event.hasEnded && (
          <Button
            variant="secondary"
            size="xxs"
            className="tw-rounded-xl"
            onClick={() => openInviteGuestsModal()}
          >
            <Icon name="user-plus" size={16} className="tw-mr-1" />
            Invite guests
          </Button>
        )}
      </div>

      <ManageDistrolistInvitesModal
        open={manageDistrolistInvitesModalOpen}
        onOpenChange={setManageDistrolistInvitesModalOpen}
      />

      <ManageCommunityInvitesModal
        open={manageCommunityInvitesModalOpen}
        onOpenChange={setManageCommunityInvitesModalOpen}
      />

      {event.isUsingEstimatedAttendees && <EventEstimatedAttendanceModal {...modalProps} />}
    </div>
  );
};

export { GuestsSectionHeader };
